import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css"; // Import the carousel styles
import { Colors } from '../../config/Constants';
import TicketCarousel from './TicketCarousal';
import { getQRCode } from '../../services/lwwTicketServices';

// Interfaces
interface TicketData {
    _id: string;
    name?: string;
    qty?: number;
    unitPrice?: number;
    qr?: { qr: string; productId: string }[];
    bookingDate?: string;
    ticketNo?: string;
    visitTime?: string;
    location?: string;
    category?: string;
    totalPrice?: number;
}

interface QRCodeData {
    _id: string;
    qr: any;
}

interface TicketCarouselModalProps {
    ticketData: TicketData[];
    id: string;
}

const TicketCarouselModal: React.FC<TicketCarouselModalProps> = ({ ticketData, id }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [qrCodeData, setQrCodeData] = useState<QRCodeData[] | null>(null);

    const fetchQRCode = async () => {
        if (isModalOpen) {
            try {
                const res = await getQRCode(id);
                setQrCodeData(res.data.data);
                ticketData.forEach((ticket) => {
                    const qrItem = res.data.data.items.find((data: any) => data._id === ticket._id);
                    if (qrItem) {
                        ticket.qr = qrItem.qr;
                    }
                });
            } catch (error) {
                console.error('Error fetching QR code:', error);
            }
        }
    };

    useEffect(() => {
        fetchQRCode();
    }, [isModalOpen, id]);

    return (
        <div>
            <OpenModalButton onClick={() => setIsModalOpen(true)}>
                View
            </OpenModalButton>

            {isModalOpen && (
                <Backdrop onClick={() => setIsModalOpen(false)}>
                    <ModalContainer onClick={(e) => e.stopPropagation()}>
                        <SliderContainer>
                            <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
                            <StyledCarousel
                                showArrows
                                showIndicators
                                showThumbs={false}
                                infiniteLoop
                                useKeyboardArrows
                                autoPlay={false}
                                emulateTouch
                            >
                                {ticketData.map((ticket) => (
                                    <CarouselItem key={ticket._id}>
                                        <TicketCarousel item={ticket} length={ticketData.length} qrCodeData={qrCodeData}
                                            index={0} />
                                    </CarouselItem>
                                ))}
                            </StyledCarousel>
                        </SliderContainer>
                    </ModalContainer>
                </Backdrop>
            )}
        </div>
    );
};


// Styled Components
const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background: ${Colors.white};
    border-radius: 16px;
    padding: 10px;
    width: 100%;
    max-width: 400px;
    /* max-height: 90vh; */
    overflow-y: auto;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px; 
    right: 10px;
    font-size: 24px;
    color: ${Colors.black};
    background: ${Colors.white}; 
    border: 1px solid ${Colors.ticketColorBlue}; 
    border-radius: 50%; 
    width: 36px; 
    height: 36px; 
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    z-index: 10; 
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.2); 
    }

    &:active {
        transform: scale(0.9);
    }
`;


const OpenModalButton = styled.button`
    padding: 10px 20px;
    font-size: 16px;
    background-color: ${Colors.ticketColorBlue};
    color: ${Colors.white};
    border: none;
    border-radius: 8px;
    cursor: pointer;
`;

const SliderContainer = styled.div`
    width: 100%;
  overflow: visible;
  position: relative;
`;

const StyledCarousel = styled(Carousel)`
    .carousel .control-next.control-arrow:before {
        content: '';
        border: solid ${Colors.ticketColorBlue};
        border-width: 0 6px 6px 0;
        display: inline-block;
        padding: 6px;
        margin-right: -2px;
        transform: rotate(-45deg);
        -webkit-transform: rotate(-45deg);
    }

    .carousel .control-prev.control-arrow:before {
        content: '';
        border: solid ${Colors.ticketColorBlue};
        border-width: 0 6px 6px 0;
        display: inline-block;
        padding: 6px;
        margin-left: -2px;
        transform: rotate(135deg);
        -webkit-transform: rotate(135deg);
    }

    .control-dots .dot {
        background-color: ${Colors.ticketColorBlue} !important; 
        opacity: 0.5 !important; 
    }

    .control-dots .dot.selected {
        background-color: ${Colors.ticketColorBlue} !important; 
        opacity: 1 !important; 
    }

    .carousel-status {
    bottom: -10px;
    display: flex;
    border-color: ${Colors.ticketColorBlue} !important;
    justify-content: flex-end;
    align-items: flex-end;
    font-weight: bold !important;
    font-family: 'Arial', sans-serif; 
    font-size: 14px; 
    margin-top: 10px; 
    text-rendering: optimizeLegibility; 
    -webkit-font-smoothing: none; 
    -moz-osx-font-smoothing: auto; 
    text-shadow: none; 
    box-shadow: none; 
    color: ${Colors.ticketColorBlue} !important; 
    background: transparent; 
    opacity: 1 !important; 
}

`;



const CarouselItem = styled.div`
    padding: 20px;
`;

export default TicketCarouselModal;
